import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { logout } from '../../../../actions/auth';
import { backendHOST } from '../../../../client-config/client-config';
import { KifizetesJogcim } from '../../../../utils/enums/adminEnums';
import { PaymentMethods } from '../../../../utils/enums/userTypes';
import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;
const urlEnd = '/Costs';

const emptyForm = {
  jogcim: '',
  datum: '',
  osszeg: '',
  descr: '',
  fiz_mod: '',
};

const FormCosts = ({ auth }) => {
  const [costs, setCosts] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const formatDateForInput = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  };

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return Number(value);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${url}${urlEnd}`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error('Nem sikerült betölteni a költségeket.');
      }

      const data = await response.json();

      setCosts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getJogcimLabel = (jogcim) => {
    const item = KifizetesJogcim.find(
      (row) => String(row.key) === String(jogcim),
    );

    return item?.name || jogcim || '';
  };

  const getPaymentMethodLabel = (paymentMethod) => {
    const item = PaymentMethods.find(
      (method) => String(method.key) === String(paymentMethod),
    );

    return item?.name || paymentMethod || '';
  };

  const sortedCosts = useMemo(() => {
    return [...costs].sort((a, b) => {
      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });
  }, [costs]);

  const filteredCosts = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedCosts;
    }

    return sortedCosts.filter((cost) =>
      [
        cost.id_bizonylat,
        cost.jogcim,
        getJogcimLabel(cost.jogcim),
        cost.datum,
        cost.osszeg,
        cost.descr,
        cost.fiz_mod,
        getPaymentMethodLabel(cost.fiz_mod),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedCosts, searchText]);

  const handleCreateClick = () => {
    setFormData({
      ...emptyForm,
      datum: formatDateForInput(new Date()),
      fiz_mod: PaymentMethods?.[0]?.key || '',
      jogcim: KifizetesJogcim?.[0]?.key || '',
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;

    setDialogOpen(false);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const normalizePayload = (data) => {
    return {
      jogcim: data.jogcim || null,
      datum: data.datum || null,
      osszeg: numberOrNull(data.osszeg),
      descr: data.descr || '',
      fiz_mod: data.fiz_mod || null,

      // Ha a backend nem tokenből állítja, akkor hasznos:
      createdBy: auth.user.ext_id_user,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = normalizePayload(formData);

      const response = await fetch(`${url}${urlEnd}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült rögzíteni a költséget.');
      }

      setMessage('Költség rögzítve.');

      setDialogOpen(false);
      setFormData(emptyForm);

      await loadData();
    } catch (err) {
      setError(err.message || 'Mentés közben hiba történt.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent='space-between'
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Költségek rögzítése
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    Egyéb kifizetések és költségek könyvelése.
                  </Typography>
                </Box>

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                >
                  Új költség
                </Button>
              </Stack>

              <TextField
                label='Keresés'
                size='small'
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ maxWidth: 320 }}
              />

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Kifizetés jogcíme</TableCell>
                        <TableCell>Kifizetés dátuma</TableCell>
                        <TableCell align='right'>Összeg</TableCell>
                        <TableCell>Megjegyzés</TableCell>
                        <TableCell>Fizetési mód</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredCosts.map((cost) => (
                        <TableRow key={cost.id_bizonylat} hover>
                          <TableCell>{getJogcimLabel(cost.jogcim)}</TableCell>

                          <TableCell>
                            {formatDateForInput(cost.datum) || '-'}
                          </TableCell>

                          <TableCell align='right'>{cost.osszeg}</TableCell>

                          <TableCell>{cost.descr}</TableCell>

                          <TableCell>
                            {getPaymentMethodLabel(cost.fiz_mod)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredCosts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align='center'>
                            Nincs megjeleníthető költség.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Új költség</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id='cost-jogcim-label'>Kifizetés jogcíme</InputLabel>
              <Select
                labelId='cost-jogcim-label'
                label='Kifizetés jogcíme'
                value={formData.jogcim}
                onChange={(event) =>
                  handleFormChange('jogcim', event.target.value)
                }
              >
                {KifizetesJogcim.map((item) => (
                  <MenuItem key={item.key} value={String(item.key)}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label='Kifizetés dátuma'
              type='date'
              value={formData.datum}
              onChange={(event) =>
                handleFormChange('datum', event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label='Összeg'
              type='number'
              value={formData.osszeg}
              onChange={(event) =>
                handleFormChange('osszeg', event.target.value)
              }
              fullWidth
            />

            <TextField
              label='Megjegyzés'
              value={formData.descr}
              onChange={(event) =>
                handleFormChange('descr', event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id='cost-payment-method-label'>
                Fizetési mód
              </InputLabel>
              <Select
                labelId='cost-payment-method-label'
                label='Fizetési mód'
                value={formData.fiz_mod}
                onChange={(event) =>
                  handleFormChange('fiz_mod', event.target.value)
                }
              >
                {PaymentMethods.map((method) => (
                  <MenuItem key={method.key} value={String(method.key)}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Mégsem
          </Button>

          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormCosts.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(withRouter(FormCosts));
